/**
 * AdminRatings — dashboard de valoraciones.
 *
 * Lista todas las valoraciones (like/dislike) enviadas por los usuarios
 * sobre imágenes generadas y copies. Permite filtrar por tipo (imagen /
 * copy) y por valoración (like / dislike). Muestra el feedback textual
 * cuando existe.
 */
import { useEffect, useMemo, useState } from "react";
import {
    ThumbsUp,
    ThumbsDown,
    Image as ImageIcon,
    Type as TypeIcon,
    MessageSquare,
    Filter,
} from "lucide-react";
import { api, thumbUrl } from "../../lib/api";

const TYPE_LABEL = { image: "Imagen", copy: "Copy" };

function fmtDate(s) {
    if (!s) return "—";
    try {
        const d = new Date(s);
        if (Number.isNaN(d.getTime())) return s;
        return d.toLocaleString("es-CO", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return s;
    }
}

export default function AdminRatings() {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState({
        total: 0,
        likes: 0,
        dislikes: 0,
        image: 0,
        copy: 0,
    });
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState("all");
    const [filterRating, setFilterRating] = useState("all");

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (filterType !== "all") params.set("item_type", filterType);
        if (filterRating !== "all") params.set("rating", filterRating);
        api.get(`/admin/ratings?${params.toString()}`)
            .then((r) => {
                setItems(r.data?.items || []);
                setStats(r.data?.stats || {});
            })
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [filterType, filterRating]);

    const ratio = useMemo(() => {
        const t = stats.total || 0;
        if (!t) return 0;
        return Math.round(((stats.likes || 0) / t) * 100);
    }, [stats]);

    return (
        <section data-testid="admin-ratings" className="space-y-6">
            {/* Stats cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard
                    label="Total"
                    value={stats.total}
                    icon={MessageSquare}
                    accent="periwinkle"
                />
                <StatCard
                    label="Likes"
                    value={stats.likes}
                    icon={ThumbsUp}
                    accent="emerald"
                />
                <StatCard
                    label="Dislikes"
                    value={stats.dislikes}
                    icon={ThumbsDown}
                    accent="rose"
                />
                <StatCard
                    label="Imágenes"
                    value={stats.image}
                    icon={ImageIcon}
                    accent="fog"
                />
                <StatCard
                    label="Copies"
                    value={stats.copy}
                    icon={TypeIcon}
                    accent="fog"
                />
            </div>

            <div className="rounded-xl border hairline border-ink-600 bg-ink-900/40 p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 text-xs text-fog-400 uppercase tracking-widest font-mono mr-1">
                        <Filter className="w-3.5 h-3.5" />
                        Filtros
                    </span>
                    <FilterChip
                        active={filterType === "all"}
                        onClick={() => setFilterType("all")}
                        label="Todos"
                        testid="filter-type-all"
                    />
                    <FilterChip
                        active={filterType === "image"}
                        onClick={() => setFilterType("image")}
                        label="Imágenes"
                        testid="filter-type-image"
                    />
                    <FilterChip
                        active={filterType === "copy"}
                        onClick={() => setFilterType("copy")}
                        label="Copies"
                        testid="filter-type-copy"
                    />
                    <span className="mx-2 text-fog-600">·</span>
                    <FilterChip
                        active={filterRating === "all"}
                        onClick={() => setFilterRating("all")}
                        label="Todas"
                        testid="filter-rating-all"
                    />
                    <FilterChip
                        active={filterRating === "like"}
                        onClick={() => setFilterRating("like")}
                        label="Likes"
                        testid="filter-rating-like"
                    />
                    <FilterChip
                        active={filterRating === "dislike"}
                        onClick={() => setFilterRating("dislike")}
                        label="Dislikes"
                        testid="filter-rating-dislike"
                    />
                </div>
                {stats.total > 0 && (
                    <p className="font-mono text-[10px] tracking-widest uppercase text-fog-400">
                        Satisfacción ·{" "}
                        <span className="text-periwinkle-800">{ratio}%</span>{" "}
                        likes
                    </p>
                )}
            </div>

            {loading ? (
                <div className="text-fog-400 text-sm">Cargando valoraciones…</div>
            ) : items.length === 0 ? (
                <div
                    data-testid="admin-ratings-empty"
                    className="rounded-xl border hairline border-ink-600 bg-ink-900/40 p-10 text-center"
                >
                    <MessageSquare className="w-8 h-8 text-fog-500 mx-auto mb-2" />
                    <p className="text-fog-300 text-sm">
                        Aún no hay valoraciones para mostrar.
                    </p>
                    <p className="text-fog-500 text-xs mt-1">
                        Los usuarios pueden valorar cada imagen o copy con
                        thumbs-up / thumbs-down. Aparecerán aquí.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((r) => (
                        <RatingRow key={r.id} r={r} />
                    ))}
                </div>
            )}
        </section>
    );
}

function StatCard({ label, value, icon: Icon, accent }) {
    const color = {
        periwinkle: "text-periwinkle-800 border-periwinkle-500/30",
        emerald: "text-emerald-400 border-emerald-500/30",
        rose: "text-rose-400 border-rose-500/30",
        fog: "text-fog-300 border-ink-600",
    }[accent];
    return (
        <div
            data-testid={`rating-stat-${label.toLowerCase()}`}
            className={`rounded-xl border hairline ${color} bg-ink-900/40 px-4 py-3 flex items-center gap-3`}
        >
            <Icon className="w-5 h-5 opacity-80" />
            <div>
                <p className="font-mono text-[9px] tracking-widest uppercase text-fog-500">
                    {label}
                </p>
                <p className="text-xl text-fog-100 font-display tabular-nums">
                    {value ?? 0}
                </p>
            </div>
        </div>
    );
}

function FilterChip({ active, onClick, label, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest transition-colors ${
                active
                    ? "bg-periwinkle-500 text-ink-on"
                    : "text-fog-400 hover:text-fog-100 border hairline border-ink-600"
            }`}
        >
            {label}
        </button>
    );
}

function RatingRow({ r }) {
    const isLike = r.rating === "like";
    const RatingIcon = isLike ? ThumbsUp : ThumbsDown;
    const isImage = r.item_type === "image";
    const TypeBadgeIcon = isImage ? ImageIcon : TypeIcon;
    return (
        <div
            data-testid={`rating-row-${r.id}`}
            className="rounded-xl border hairline border-ink-600 bg-ink-900/40 p-4 flex gap-4"
        >
            {/* Preview */}
            <div className="shrink-0">
                {isImage && r.item_preview ? (
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-ink-800 border hairline border-ink-600">
                        <img
                            src={thumbUrl(r.item_preview, 160)}
                            alt="preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.currentTarget.style.display = "none";
                            }}
                        />
                    </div>
                ) : (
                    <div className="w-20 h-20 rounded-lg bg-ink-800 border hairline border-ink-600 flex items-center justify-center">
                        <TypeBadgeIcon className="w-6 h-6 text-fog-500" />
                    </div>
                )}
            </div>

            {/* Body */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest ${
                            isLike
                                ? "bg-emerald-500/10 text-emerald-400 border hairline border-emerald-500/30"
                                : "bg-rose-500/10 text-rose-400 border hairline border-rose-500/30"
                        }`}
                    >
                        <RatingIcon className="w-3 h-3" />
                        {isLike ? "Like" : "Dislike"}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest border hairline border-ink-600 text-fog-300">
                        <TypeBadgeIcon className="w-3 h-3" />
                        {TYPE_LABEL[r.item_type] || r.item_type}
                    </span>
                    {r.chat_title && (
                        <span
                            className="text-[10px] font-mono uppercase tracking-widest text-fog-400 truncate"
                            title={r.chat_title}
                        >
                            · {r.chat_title}
                        </span>
                    )}
                </div>
                <p className="text-sm text-fog-100 mt-1.5">
                    <span className="font-medium">{r.user_name || r.user_email}</span>
                    <span className="text-fog-500 ml-2 text-xs">
                        {r.user_email}
                    </span>
                </p>
                {!isImage && r.item_preview && (
                    <p className="text-xs text-fog-400 mt-1 line-clamp-2 italic">
                        “{r.item_preview}”
                    </p>
                )}
                {r.feedback && (
                    <div className="mt-2 rounded-lg bg-ink-950/60 border-l-2 border-rose-500/60 px-3 py-2">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-rose-400 mb-0.5">
                            Comentario
                        </p>
                        <p className="text-sm text-fog-200 whitespace-pre-wrap">
                            {r.feedback}
                        </p>
                    </div>
                )}
            </div>

            <div className="shrink-0 text-right">
                <p className="font-mono text-[10px] tracking-widest uppercase text-fog-500">
                    {fmtDate(r.updated_at || r.created_at)}
                </p>
            </div>
        </div>
    );
}
